import { useRef, useState } from 'react';

/* The conductor for a staged demo. Owns the loop's lifecycle concerns so each
   stage script is pure choreography:

   - `run(script)` is called from useMountEffect and returns the cleanup that
     kills the loop; the script receives helpers and is responsible for its
     own `while (h.alive())` loop.
   - `gate()` parks the script while the stage is paused or offscreen — the
     stage wires `viewportProps` onto its root motion element, and the pause
     button (WCAG 2.2.2: autoplaying motion needs a control) flips `paused`.
     Refs, not state, so parking never re-renders.
   - Under prefers-reduced-motion the CALLER seeds its state to the finished
     frame and simply never calls `run` — same convention as the marketing
     demos. */

export interface StageHelpers {
    alive: () => boolean;
    wait: (ms: number) => Promise<void>;
    /** Awaits while the stage is paused or offscreen. Call at the top of every
     *  loop iteration (and before long beats) so a hidden stage costs nothing. */
    gate: () => Promise<void>;
}

export function useStagePlayer() {
    const inViewRef = useRef(false);
    const pausedRef = useRef(false);
    const [paused, setPaused] = useState(false);

    const run = (script: (h: StageHelpers) => Promise<void>): (() => void) => {
        let alive = true;
        const wait = (ms: number) =>
            new Promise<void>((res) => window.setTimeout(res, ms));
        const gate = async () => {
            while (alive && (pausedRef.current || !inViewRef.current)) {
                await wait(220);
            }
        };
        void script({ alive: () => alive, wait, gate });
        return () => {
            alive = false;
        };
    };

    const togglePaused = () => {
        pausedRef.current = !pausedRef.current;
        setPaused(pausedRef.current);
    };

    const viewportProps = {
        onViewportEnter: () => {
            inViewRef.current = true;
        },
        onViewportLeave: () => {
            inViewRef.current = false;
        },
    };

    return { run, paused, togglePaused, viewportProps };
}
